import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { SunIcon, WalkIcon, PillIcon, EnergyIcon, SleepIcon, PlanIcon, AnalyticsIcon, MaterialsIcon, ProfileIcon } from './Icons'

const Hero = () => {
  const { t, getLangPrefix } = useLanguage()
  const langPrefix = getLangPrefix()
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  })

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  }

  return (
    <section ref={ref} className="relative min-h-[90vh] md:min-h-screen flex md:items-start overflow-hidden bg-gradient-to-b from-white via-emerald-50/30 to-white pt-2 md:pt-4 lg:pt-6 pb-8 md:pb-0">
      <div className="absolute inset-0 overflow-hidden z-0">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-primary/20 to-primary-dark/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-primary-light/20 to-primary/10 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto px-6 sm:px-6 lg:px-8 relative z-20 py-6 sm:py-8 md:py-6">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 xl:gap-16 lg:items-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ y, opacity }}
          >
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight mb-4 sm:mb-5 md:mb-6"
            >
              {t.hero.title.split(/(Longevity)/i).map((part, index) => 
                part.toLowerCase() === 'longevity' ? (
                  <span key={index} className="gradient-text bg-gradient-to-r from-primary via-primary-dark to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                    {part}
                  </span>
                ) : (
                  <span key={index}>{part}</span>
                )
              )}
              {t.hero.titleHighlight && (
                <>
                  {' '}
                  <span className="gradient-text bg-gradient-to-r from-primary via-primary-dark to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                    {t.hero.titleHighlight}
                  </span>
                </>
              )}
              {t.hero.titleEnd && (
                <>
                  <br />
                  {t.hero.titleEnd}
                </>
              )}
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl md:text-xl lg:text-2xl text-gray-600 mb-6 sm:mb-7 md:mb-6 leading-relaxed"
            >
              {t.hero.description}
            </motion.p>


            <motion.div
              variants={itemVariants}
              className="mb-6 sm:mb-8 md:mb-8"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to={`${langPrefix}/waitlist`}
                  className="inline-block w-full sm:w-auto text-center px-8 sm:px-8 md:px-8 py-4 sm:py-4 md:py-4 gradient-bg text-white rounded-xl md:rounded-2xl font-bold text-lg sm:text-lg md:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 premium-glow"
                >
                  {t.hero.tryFree}
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 lg:gap-6"
            >
              {[
                { number: '$99', label: t.hero.price },
                { number: '100%', label: t.hero.automatic },
                { number: '24/7', label: t.hero.personalPlan },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 + index * 0.2 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  className="w-full"
                >
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-2.5 sm:p-3 md:p-5 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 h-full flex flex-col items-center justify-center text-center">
                    <motion.div
                      className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-black gradient-text mb-1 sm:mb-1.5 md:mb-2"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.3,
                      }}
                    >
                      {stat.number}
                    </motion.div>
                    <div className="text-[10px] sm:text-xs md:text-base text-gray-600 font-medium leading-tight px-0.5">
                      {stat.label}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="relative hidden lg:block"
          >
            <PhoneMockup t={t} />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="lg:hidden mt-8 mb-4"
          >
            <div className="relative bg-gradient-to-br from-emerald-50/50 to-primary/10 rounded-2xl p-4 border border-primary/20 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.1),transparent_70%)]" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs font-semibold text-primary">{t.phoneMockup?.progress || 'Progress'}</span>
                </div>
                <div className="relative h-24">
                  <div className="flex items-end justify-between h-full gap-1">
                    {[65, 72, 68, 78, 75, 82, 88, 85, 90, 87, 92, 95].map((value, index) => (
                      <motion.div
                        key={index}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: `${value}%`, opacity: 1 }}
                        transition={{
                          delay: 1.6 + index * 0.1,
                          duration: 0.5,
                          ease: 'easeOut',
                        }}
                        className="flex-1 bg-gradient-to-t from-primary via-primary-dark to-primary rounded-t-lg relative group"
                      >
                        <motion.div
                          animate={{
                            opacity: [0.3, 0.6, 0.3],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: index * 0.1,
                          }}
                          className="absolute inset-0 bg-white/20 rounded-t-lg"
                        />
                      </motion.div>
                    ))}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
  )
}

const PhoneMockup = ({ t }) => {
  return (
    <motion.div
      animate={{
        y: [0, -20, 0],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      className="relative flex justify-center items-center"
    >
      <motion.div
        whileHover={{ scale: 1.05, rotateY: 5 }}
        transition={{ duration: 0.3 }}
        className="relative w-[240px] h-[480px] sm:w-[280px] sm:h-[560px] md:w-[320px] md:h-[640px] lg:w-[360px] lg:h-[720px] bg-gradient-to-br from-gray-900 to-black rounded-[32px] sm:rounded-[36px] md:rounded-[40px] lg:rounded-[48px] p-1 sm:p-1.5 md:p-2 shadow-2xl premium-glow"
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        <div className="absolute top-3 sm:top-4 md:top-6 left-1/2 -translate-x-1/2 w-[80px] sm:w-[100px] md:w-[120px] h-5 sm:h-6 md:h-8 bg-black rounded-full z-10" />
        <div className="w-full h-full bg-gradient-to-b from-emerald-50 to-white rounded-[28px] sm:rounded-[32px] md:rounded-[36px] lg:rounded-[42px] overflow-hidden relative">
          <div className="absolute top-0 left-0 right-0 h-12 sm:h-14 md:h-16 lg:h-20 gradient-bg text-white p-3 sm:p-4 md:p-6 pt-8 sm:pt-10 md:pt-12 flex justify-between items-center text-[10px] sm:text-xs md:text-sm font-semibold">
            <span>09:42</span>
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="flex items-end gap-0.5">
                <div className="w-0.5 h-1.5 md:w-1 md:h-2 bg-white rounded-sm" />
                <div className="w-0.5 h-2 md:w-1 md:h-2.5 bg-white rounded-sm" />
                <div className="w-0.5 h-2.5 md:w-1 md:h-3 bg-white rounded-sm" />
                <div className="w-0.5 h-2 md:w-1 md:h-2.5 bg-white/60 rounded-sm" />
              </div>
              <span className="text-xs md:text-sm">85%</span>
              <div className="relative">
                <div className="w-5 h-2.5 md:w-6 md:h-3 border-2 border-white rounded-sm" />
                <div className="absolute left-0.5 top-0.5 bottom-0.5 w-[70%] md:w-[75%] bg-white rounded-sm" />
                <div className="absolute right-[-2px] top-1/2 -translate-y-1/2 w-0.5 h-1.5 md:h-2 bg-white rounded-r-sm" />
              </div>
            </div>
          </div>

          <div className="pt-12 sm:pt-16 md:pt-20 lg:pt-24 px-3 sm:px-4 md:px-5 pb-16 sm:pb-20 md:pb-24 space-y-2 sm:space-y-3 md:space-y-4 overflow-y-auto h-full">
            <div>
              <div className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-0.5 sm:mb-1">
                {t.phoneMockup?.goodMorning || 'Good morning!'}
              </div>
              <div className="text-[10px] sm:text-xs md:text-sm text-gray-500">{t.phoneMockup?.date || 'Friday, January 30'}</div>
            </div>

            <div className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">
              {t.phoneMockup?.yourPlanToday || 'Your plan for today'}
            </div>

            {[
              {
                icon: <SunIcon className="w-5 h-5 md:w-6 md:h-6" />,
                title: t.phoneMockup?.task1 || 'Get sunlight before 10:00',
                status: t.phoneMockup?.task1Status || 'Completed',
                completed: true,
              },
              {
                icon: <WalkIcon className="w-5 h-5 md:w-6 md:h-6" />,
                title: t.phoneMockup?.task2 || 'Walk 20 min',
                status: t.phoneMockup?.task2Status || 'In progress',
                active: true,
              },
              {
                icon: <PillIcon className="w-5 h-5 md:w-6 md:h-6" />,
                title: t.phoneMockup?.task3 || 'Magnesium before bed',
                status: t.phoneMockup?.task3Status || 'Scheduled • 22:00',
              },
            ].map((task, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5 + index * 0.2 }}
                whileHover={{ x: 4 }}
                className={`p-3 md:p-4 rounded-xl md:rounded-2xl ${
                  task.completed
                    ? 'bg-emerald-100 border-2 border-primary'
                    : task.active
                    ? 'bg-amber-50 border-2 border-amber-400'
                    : 'bg-emerald-50 border-2 border-transparent'
                } flex items-center justify-between`}
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex-shrink-0">{task.icon}</div>
                  <div>
                    <div className="font-semibold text-xs md:text-sm text-gray-900">
                      {task.title}
                    </div>
                    <div
                      className={`text-[10px] md:text-xs ${
                        task.completed
                          ? 'text-primary font-semibold'
                          : task.active
                          ? 'text-amber-600 font-semibold'
                          : 'text-gray-500'
                      }`}
                    >
                      {task.status}
                    </div>
                  </div>
                </div>
                <div
                  className={`w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    task.completed ? 'bg-primary' : 'border-2 border-gray-300'
                  }`}
                >
                  {task.completed && (
                    <svg
                      className="w-3 h-3 md:w-4 md:h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </motion.div>
            ))}

            <div className="grid grid-cols-2 gap-2 md:gap-3 mt-4 md:mt-6">
              {[
                { icon: <SleepIcon className="w-6 h-6 md:w-8 md:h-8" />, label: t.phoneMockup?.sleep || 'Sleep', value: '7h 42m' },
                { icon: <EnergyIcon className="w-6 h-6 md:w-8 md:h-8" />, label: t.phoneMockup?.energy || 'Energy', value: '85%' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 2 + index * 0.1 }}
                  className="gradient-bg text-white p-3 md:p-4 rounded-xl md:rounded-2xl text-center"
                >
                  <div className="flex justify-center mb-1 md:mb-2">{stat.icon}</div>
                  <div className="text-[10px] md:text-xs opacity-90 mb-1">{stat.label}</div>
                  <div className="text-base md:text-xl font-bold">{stat.value}</div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200/50 shadow-lg flex justify-around items-center py-2 md:py-3 px-2">
            {[
              { label: t.phoneMockup?.myPlan || 'My Plan', icon: PlanIcon, active: true },
              { label: t.phoneMockup?.analytics || 'Analytics', icon: AnalyticsIcon, active: false },
              { label: t.phoneMockup?.materials || 'Materials', icon: MaterialsIcon, active: false },
              { label: t.phoneMockup?.profile || 'Profile', icon: ProfileIcon, active: false },
            ].map((item, index) => {
              const IconComponent = item.icon
              return (
                <motion.button
                  key={index}
                  whileTap={{ scale: 0.9 }}
                  className={`flex flex-col items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1 rounded-lg transition-all duration-200 ${
                    item.active
                      ? 'text-primary'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <IconComponent
                    className={`w-5 h-5 md:w-6 md:h-6 ${
                      item.active ? 'text-primary' : 'text-gray-400'
                    }`}
                    active={item.active}
                  />
                  <span
                    className={`text-[9px] md:text-[10px] font-semibold leading-tight ${
                      item.active ? 'text-primary' : 'text-gray-400'
                    }`}
                  >
                    {item.label}
                  </span>
                </motion.button>
              )
            })}
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default Hero
